import type {
  ResolvedAnswers,
  Category,
  PlaybookId,
  ResultContent,
  PersonalisationField,
} from './types';
import { capacityMinutes, optionLabel, personalisationFields } from './questions';

export interface PlaybookMeta {
  id: PlaybookId;
  name: string;
  category: Category;
  coreHeadline: string;
  /** Internal review only. */
  problemAddressed: string;
  triggerPatterns: string[];
  gatesRequired: string[];
  assetProduced: string;
  mustNotClaim: string[];
  blockingCases: string[];
}

export interface Playbook extends PlaybookMeta {
  personalisation: PersonalisationField[];
  build: (a: ResolvedAnswers) => ResultContent;
}

// ---------- small helpers ----------

const p = (a: ResolvedAnswers, key: string, fallback = '') => (a.personalisation[key] || '').trim() || fallback;
const has = <T,>(list: T[], v: T) => list.includes(v);
const songName = (a: ResolvedAnswers) => (a.songTitle.trim() ? a.songTitle.trim() : 'this song');
const effort = (a: ResolvedAnswers) => capacityMinutes[a.capacity ?? 'SMALL'] ?? 45;
const missing = (a: ResolvedAnswers, ...keys: string[]) =>
  keys.filter((k) => !(a.personalisation[k] || '').trim());

const selectLabel = (category: Category, fieldId: string, value: string) => {
  const field = personalisationFields[category].find((f) => f.id === fieldId);
  return field?.options?.find((o) => o.id === value)?.label ?? value;
};

const readinessNote = (a: ResolvedAnswers) =>
  a.readiness === 'RELEASED'
    ? `${songName(a)} is already released, so the invitation can point at it directly.`
    : a.readiness === 'UNRELEASED_SHAREABLE'
    ? `${songName(a)} is not released yet, so nothing here asks anyone to stream it.`
    : a.readiness === 'CHOOSE_SONG'
    ? 'You have not picked one song for this yet, so the plan uses the first song you choose.'
    : 'You told us there is no shareable song or excerpt yet.';

const goalPhrase = (a: ResolvedAnswers) =>
  ({
    FIRST_LISTENERS: 'getting the first people genuinely interested',
    MORE_DISCOVERY: 'reaching listeners beyond the people who already know you',
    NEXT_RELEASE: 'giving an upcoming release a clearer plan',
    MORE_RETURN: 'getting interested listeners to come back',
    LOCAL_SHOW: 'building interest in a particular show',
  }[a.goal ?? 'MORE_DISCOVERY']);

const filmingLine = (format: string) =>
  ({
    FACE: 'Film yourself performing the moment, framed from the chest up. One take is enough.',
    HANDS: 'Film your hands and the instrument only. No face, no speaking.',
    ARTWORK: 'Use artwork or footage you already have, held on screen while the audio plays.',
    AUDIO: 'Use the audio over a single still image. No new filming required.',
    UNSURE: 'Use the simplest version you can make today: audio over one still image.',
  }[format] ?? 'Use the simplest version you can make today: audio over one still image.');

// ---------- playbook definitions ----------

export const playbooks: Record<PlaybookId, Playbook> = {
  // ================= AUDIENCE =================
  A01: {
    id: 'A01',
    name: "From 'anyone' to a first listener hypothesis",
    category: 'AUDIENCE',
    coreHeadline: 'GIVE ONE KIND OF LISTENER A CLEAR REASON TO CARE.',
    problemAddressed:
      'The artist has not chosen any listener group for this song, so every introduction has to work for everyone at once.',
    triggerPatterns: ['audience = EVERYONE', 'audience = UNSURE', 'descriptive musical input supplied'],
    gatesRequired: [
      'Shareable music exists (readiness is not NO_SHAREABLE)',
      'No confirmed broken or missing destination that would take priority',
      'Enough supplied musical detail to propose a reasoned starting point',
    ],
    assetProduced:
      'Up to two provisional listener groups with the musical reason for each, plus exact introduction copy for the recommended one.',
    mustNotClaim: [
      'No invented age, gender, job, country or guaranteed fan pool',
      'Not presented as a definitive audience profile',
      'Organic exposure is not described as a controlled audience test',
    ],
    blockingCases: [
      'A confirmed absent or mismatched destination (N01 first)',
      'No shareable music (B02)',
      'A blank analytics field alone, with no supplied musical context',
    ],
    personalisation: personalisationFields.AUDIENCE,
    build: (a) => {
      const sound = p(a, 'sound_description', 'the sound you described');
      const keep = p(a, 'keep_making');
      return {
        headline: 'START WITH ONE LISTENER GROUP, NOT EVERYONE AT ONCE.',
        startHereLabel: 'The Audience Fix',
        startHereBody: `You said you are aiming at anyone who might like ${songName(a)}. The first test is a single provisional listener group, described through what they already choose to listen to, and one introduction written for them.`,
        provisionalNote: 'This is the first thing we would test, not proof of the cause.',
        basis: [
          {
            answerRef: 'Q2 audience',
            paraphrase: optionLabel('audience', a.audience ?? 'EVERYONE'),
            basisType: 'ANSWER_PATTERN',
          },
          {
            answerRef: 'Personalisation: sound',
            paraphrase: sound,
            basisType: a.personalisation.sound_description ? 'SPECIFIC_REPORTED_EXAMPLE' : 'LIMITED_INFORMATION',
          },
        ],
        asset: {
          title: 'Your listener plan',
          copyButtonLabel: 'COPY MY LISTENER PLAN',
          readiness: 'READY_TO_USE',
          blocks: [
            {
              heading: 'Provisional listener group (start here)',
              body: `People who already seek out music described as: ${sound}. They are defined by listening choice, not by age or location. We do not yet know whether they are reachable in the places you post.`,
            },
            {
              heading: 'The musical reason',
              body: `You described ${songName(a)} as ${sound}. That is the feature the introduction leads with, rather than a genre label.`,
            },
            {
              heading: 'Second group, only if the first gets no response',
              body: keep
                ? `People who come for ${keep}. Hold the same excerpt and change only the opening line.`
                : 'Not proposed. You did not supply a second distinct musical angle, and inventing one would not be useful.',
            },
            {
              heading: 'Introduction copy to use',
              body: `If you like ${sound}, this is the part of ${songName(a)} to hear first.`,
            },
            {
              heading: 'What we do not know',
              body: 'Whether this group is actually present in the place you post, and whether the excerpt is the strongest entry point. Both only become clear after an attempt.',
            },
          ],
          prerequisiteChecks: ['Choose the single excerpt you will hold constant across attempts.'],
        },
        firstAction: {
          instruction: `Post or share the excerpt once using the introduction copy above, in one context you already use. Change nothing else.`,
          approximateEffortMinutes: effort(a),
        },
        measurement: {
          mainObservation: 'Replies that mention the music itself rather than general praise.',
          window: '7 days from posting, then a comparable repeat.',
          limitation: 'Organic reach varies between posts, so one attempt cannot confirm the group is right.',
        },
        decisionRule: {
          repeatWhen: 'You get music-specific replies and want to see whether they appear again.',
          adjustWhen: 'You get attention but nothing about the music. Change the opening line, not the excerpt.',
          insufficientInformationWhen: 'Almost nobody saw it. That is a reach question, not an audience answer.',
        },
        whatCanWait: {
          activity: 'Rewriting your bio, artwork or overall positioning.',
          reason: 'Nothing in your answers points at your presentation. You have not yet tested a single specific listener angle.',
        },
        missingInputs: missing(a, 'sound_description', 'keep_making'),
      };
    },
  },

  A02: {
    id: 'A02',
    name: 'Turn a reference name into a musical reason',
    category: 'AUDIENCE',
    coreHeadline: 'GIVE ONE KIND OF LISTENER A CLEAR REASON TO CARE.',
    problemAddressed:
      'Targeting rests on reference artists or subject matter rather than a musical reason a listener would act on.',
    triggerPatterns: ['audience = REFERENCE_NAMES', 'audience = TOPIC_FIRST'],
    gatesRequired: [
      'Shareable music exists',
      'No confirmed destination problem taking priority',
      'Supplied sound description or reference detail to work from',
    ],
    assetProduced:
      'A comparison map separating sound, subject and presentation, plus feature-led introduction copy that does not lean on a famous name.',
    mustNotClaim: [
      'No claimed knowledge of a reference song we have not heard',
      'Reference-artist fans are not described as a ready-made audience',
    ],
    blockingCases: ['Confirmed broken destination (N01)', 'No shareable music (B02)'],
    personalisation: personalisationFields.AUDIENCE,
    build: (a) => {
      const sound = p(a, 'sound_description', 'the sound you described');
      const ref = p(a, 'reference_detail');
      return {
        headline: 'LEAD WITH THE MUSICAL FEATURE, NOT THE FAMOUS NAME.',
        startHereLabel: 'The Audience Fix',
        startHereBody: `You are describing your listener through ${a.audience === 'TOPIC_FIRST' ? 'the subject of the song' : 'other artists and a broad genre'}. The first test is an introduction built on a feature someone can hear in ${songName(a)}.`,
        provisionalNote: 'This is the first thing we would test, not proof of the cause.',
        basis: [
          {
            answerRef: 'Q2 audience',
            paraphrase: optionLabel('audience', a.audience ?? 'REFERENCE_NAMES'),
            basisType: 'ANSWER_PATTERN',
          },
          {
            answerRef: 'Personalisation: references',
            paraphrase: ref || 'No reference detail supplied, so the plan uses your own sound description only.',
            basisType: ref ? 'SPECIFIC_REPORTED_EXAMPLE' : 'LIMITED_INFORMATION',
          },
        ],
        asset: {
          title: 'Your listener plan',
          copyButtonLabel: 'COPY MY LISTENER PLAN',
          readiness: 'READY_TO_USE',
          blocks: [
            {
              heading: 'Comparison map (your words, not our judgement)',
              body: `Sound: ${sound}\nSubject or theme: ${p(a, 'keep_making', 'not supplied')}\nPresentation and references: ${ref || 'not supplied'}`,
            },
            {
              heading: 'The listener hypothesis',
              body: `Someone who chooses music for this specific quality: ${sound}. Not everyone who likes the artists you named.`,
            },
            {
              heading: 'Introduction copy to use',
              body: `This part of ${songName(a)} is built around ${sound}. Thirty seconds is enough to tell if that is your thing.`,
            },
            {
              heading: 'What this does not establish',
              body: 'Your comparison is your own description. We have not listened to your song or to any reference track.',
            },
          ],
          prerequisiteChecks: ['Pick the excerpt that most clearly demonstrates the feature you named.'],
        },
        firstAction: {
          instruction: 'Use the feature-led introduction once, holding the same excerpt you would have used anyway.',
          approximateEffortMinutes: effort(a),
        },
        measurement: {
          mainObservation: 'Comments about the musical feature or the song, rather than about the artist you referenced.',
          window: '7 days, then one comparable repeat.',
          limitation: 'A name-led post and a feature-led post rarely get equal reach, so treat this as a direction, not a verdict.',
        },
        decisionRule: {
          repeatWhen: 'People respond to the feature. Repeat with a second excerpt that shares it.',
          adjustWhen: 'Responses still centre on the reference artist. Try a different feature from your description.',
          insufficientInformationWhen: 'Too few people saw it to read anything.',
        },
        whatCanWait: {
          activity: 'Choosing a permanent genre label for the project.',
          reason: 'You are testing one song. A fixed label is a decision you can make after a few attempts tell you something.',
        },
        missingInputs: missing(a, 'sound_description'),
      };
    },
  },

  A03: {
    id: 'A03',
    name: 'Give a mixed sound a recognisable entry point',
    category: 'AUDIENCE',
    coreHeadline: 'GIVE ONE KIND OF LISTENER A CLEAR REASON TO CARE.',
    problemAddressed:
      'The music genuinely crosses styles, or a varied catalogue is being promoted all at once, and no single entry point exists.',
    triggerPatterns: [
      'audience = UNSURE or EVERYONE combined with readiness = CHOOSE_SONG',
      'supplied description crosses styles',
    ],
    gatesRequired: ['Supplied musical description showing more than one style', 'No confirmed destination problem'],
    assetProduced: 'Two possible entry points with trade-offs, a lead introduction and an alternate.',
    mustNotClaim: [
      'Blending genres does not automatically supply both audiences',
      'Not inferred from an "alternative" genre label alone',
    ],
    blockingCases: ['Confirmed broken destination (N01)', 'No shareable music (B02)'],
    personalisation: personalisationFields.AUDIENCE,
    build: (a) => {
      const sound = p(a, 'sound_description', 'the mix of styles you described');
      const keep = p(a, 'keep_making', 'the part you said you most want to keep making');
      return {
        headline: 'PICK ONE FAMILIAR WAY IN, WITHOUT REMOVING THE UNUSUAL PART.',
        startHereLabel: 'The Audience Fix',
        startHereBody: `You have music that crosses styles and no single song chosen to lead with. The first test is one recognisable entry point, kept for a few attempts.`,
        provisionalNote: 'This is the first thing we would test, not proof of the cause.',
        basis: [
          { answerRef: 'Q1 readiness', paraphrase: optionLabel('readiness', a.readiness ?? 'CHOOSE_SONG'), basisType: 'ANSWER_PATTERN' },
          { answerRef: 'Personalisation: sound', paraphrase: sound, basisType: 'SPECIFIC_REPORTED_EXAMPLE' },
        ],
        asset: {
          title: 'Your listener plan',
          copyButtonLabel: 'COPY MY LISTENER PLAN',
          readiness: 'READY_TO_USE',
          blocks: [
            { heading: 'Entry point A (lead with this)', body: `${keep}. It is the part you said you want to keep making, so repeating it does not pull you away from the music you want to write.` },
            { heading: 'Entry point B (alternate)', body: `The contrasting element in: ${sound}. More distinctive, harder to describe in one line.` },
            { heading: 'Trade-off', body: 'A is easier for someone to recognise in three seconds. B is more memorable if they stay. Test A first because it costs less attention.' },
            { heading: 'Lead introduction copy', body: `Start here if you want ${keep}. The rest of ${songName(a)} goes somewhere else.` },
            { heading: 'How this relates to the wider catalogue', body: 'This is an entry point for one song, not a decision about everything you release.' },
          ],
          prerequisiteChecks: ['Choose the one song this plan applies to before posting.'],
        },
        firstAction: { instruction: 'Choose one song, then make one post using entry point A.', approximateEffortMinutes: effort(a) },
        measurement: {
          mainObservation: 'Whether anyone asks about or references the specific element you led with.',
          window: '10 days across two attempts.',
          limitation: 'Two organic posts cannot separate the entry point from the reach they happened to get.',
        },
        decisionRule: {
          repeatWhen: 'Entry point A draws music-specific replies. Repeat with a second song that shares it.',
          adjustWhen: 'No response at all after two attempts. Try entry point B with the same excerpt length.',
          insufficientInformationWhen: 'You have not yet settled on one song, so nothing is comparable.',
        },
        whatCanWait: { activity: 'Rebranding the project around one genre.', reason: 'You have not yet tested whether either entry point changes how people respond.' },
        missingInputs: missing(a, 'sound_description', 'keep_making'),
      };
    },
  },

  // ================= DISCOVERY =================
  D01: {
    id: 'D01',
    name: 'Move beyond the existing circle',
    category: 'DISCOVERY',
    coreHeadline: 'GIVE NEW LISTENERS A WAY IN.',
    problemAddressed: 'All current response comes from people who already know the artist, or no deliberate route to new people exists.',
    triggerPatterns: ['discovery = EXISTING_CIRCLE', 'discovery = NOT_STARTED'],
    gatesRequired: ['Shareable music exists', 'No confirmed destination problem taking priority'],
    assetProduced: 'One chosen discovery route, a specific contribution, a ready draft and the first place to check.',
    mustNotClaim: [
      'No indiscriminate link dropping or cold messages to ordinary listeners',
      'A curator reply is access evidence, not a fan',
    ],
    blockingCases: ['No shareable music (B02)', 'Confirmed broken destination (N01)'],
    personalisation: personalisationFields.DISCOVERY,
    build: (a) => {
      const route = p(a, 'route_choice', 'OWN_POSTS');
      const music = p(a, 'music_description', 'the music you described');
      const access = p(a, 'existing_access', 'none yet');
      const routeText = selectLabel('DISCOVERY', 'route_choice', route);
      return {
        headline: 'MAKE ONE INTRODUCTION OUTSIDE THE PEOPLE WHO ALREADY KNOW YOU.',
        startHereLabel: 'The Discovery Fix',
        startHereBody: `The response you described is coming from people already around you. The first test is one deliberate introduction to people who have never heard ${songName(a)}.`,
        basis: [
          { answerRef: 'Q3 discovery', paraphrase: optionLabel('discovery', a.discovery ?? 'EXISTING_CIRCLE'), basisType: 'ANSWER_PATTERN' },
          { answerRef: 'Personalisation: access', paraphrase: access, basisType: access !== 'none yet' ? 'SPECIFIC_REPORTED_EXAMPLE' : 'LIMITED_INFORMATION' },
        ],
        asset: {
          title: 'Your outreach draft',
          copyButtonLabel: 'COPY MY OUTREACH DRAFT',
          readiness: 'NEEDS_RECIPIENT_OR_DESTINATION_CHECK',
          blocks: [
            { heading: 'Route for the next two weeks', body: `${routeText}. Chosen because it is the one you said you could realistically do, and it fits ${goalPhrase(a)}.` },
            { heading: 'Your contribution', body: `A ${music} piece, offered as something their audience would actually want to hear, not as a request for support.` },
            {
              heading: 'Draft message (fill the bracket yourself)',
              body: `Hi [name],\n\nI make ${music}. I have one track, ${songName(a)}, that fits what you [specific thing they do - check this first].\n\nIf it is useful I can send the file and a short line about it. If not, no problem at all.\n\n[your name]`,
            },
            { heading: 'Two searches to find candidates', body: `"${music}" submission guidelines\n"${music}" new artist feature` },
            { heading: 'Honest label', body: 'We have not researched or verified any recipient. These are searches for you to run, not a vetted list.' },
          ],
          prerequisiteChecks: [
            'Check one recipient\'s recent work and their submission rules before sending.',
            'Replace every bracket with something you have actually confirmed.',
          ],
        },
        firstAction: { instruction: 'Find and check one fitting recipient or opportunity, then decide whether to send.', approximateEffortMinutes: effort(a) },
        measurement: {
          mainObservation: 'Whether the route actually put the music in front of unfamiliar people, and then any song-specific response.',
          window: '14 days.',
          limitation: 'A reply is access, not a listener. Count them separately.',
        },
        decisionRule: {
          repeatWhen: 'You reached unfamiliar people at all. Repeat with two more recipients before changing anything.',
          adjustWhen: 'No replies from five checked, well-matched recipients. Change the contribution, not the song.',
          insufficientInformationWhen: 'You did not get to send anything. Reduce the step, do not change the plan.',
        },
        whatCanWait: { activity: 'Paid promotion.', reason: 'You have not yet made one deliberate unpaid introduction, so there is nothing to scale.' },
        missingInputs: missing(a, 'music_description', 'existing_access'),
      };
    },
  },

  D02: {
    id: 'D02',
    name: 'Make a collaboration or submission relevant to the recipient',
    category: 'DISCOVERY',
    coreHeadline: 'GIVE NEW LISTENERS A WAY IN.',
    problemAddressed: 'A collaboration or submission route is chosen but the approach does not explain why the music fits that recipient.',
    triggerPatterns: ['discovery = COLLAB or CURATION', 'personalisation route = OTHER_CREATOR or CURATOR'],
    gatesRequired: ['Artist has chosen this route', 'A real recipient supplied or willingness to research one'],
    assetProduced: 'A specific proposed contribution, a short request, the minimum material needed and a recipient-fit checklist.',
    mustNotClaim: [
      'No faked familiarity or invented "I loved your recent..." line',
      'No promise of exposure in return',
      'Never implied that any opportunity has been verified',
    ],
    blockingCases: ['No shareable music (B02)', 'Confirmed broken destination (N01)'],
    personalisation: personalisationFields.DISCOVERY,
    build: (a) => {
      const music = p(a, 'music_description', 'the music you described');
      const access = p(a, 'existing_access', 'none yet');
      const named = access.toLowerCase() !== 'none yet' && access.length > 3;
      return {
        headline: 'WRITE THE APPROACH AROUND WHAT THEY ALREADY PUT OUT.',
        startHereLabel: 'The Discovery Fix',
        startHereBody: `You are using introductions through other people. The first test is one approach that explains, in a line, why ${songName(a)} fits what the recipient already reaches.`,
        basis: [
          { answerRef: 'Q3 discovery', paraphrase: optionLabel('discovery', a.discovery ?? 'COLLAB'), basisType: 'ANSWER_PATTERN' },
          { answerRef: 'Personalisation: opportunity', paraphrase: named ? access : 'No named opportunity yet, so this includes a search plan instead of a contact.', basisType: named ? 'SPECIFIC_REPORTED_EXAMPLE' : 'LIMITED_INFORMATION' },
        ],
        asset: {
          title: 'Your outreach draft',
          copyButtonLabel: 'COPY MY OUTREACH DRAFT',
          readiness: 'NEEDS_RECIPIENT_OR_DESTINATION_CHECK',
          blocks: [
            { heading: 'What you are offering', body: `${music}, supplied ready to use: the finished file, a one-line description and an image. Nothing they have to chase you for.` },
            {
              heading: 'The message',
              body: `Hi [name],\n\n[One sentence about a specific thing they actually released or featured - check this yourself, we have not looked.]\n\nI have a track called ${songName(a)}. It is ${music}. I think it sits alongside that rather than against it.\n\nHappy to send the file, or to leave it here if it is not right.\n\n[your name]`,
            },
            { heading: 'Recipient-fit checklist', body: '1. Have they featured an unsigned artist in the last two months?\n2. Do they state submission rules anywhere? Follow them exactly.\n3. Does the music they already feature sit near yours, in your own honest judgement?\n4. Are you able to supply what they ask for today?' },
            { heading: 'If you have no name yet', body: `Run these two searches:\n"${music}" submissions open\n"${music}" artist feature ${a.goal === 'LOCAL_SHOW' ? '[your city]' : '[your scene]'}` },
          ],
          prerequisiteChecks: [
            'The opening sentence must describe something you have personally seen.',
            'Confirm the submission route they actually ask for.',
          ],
        },
        firstAction: { instruction: 'Check one recipient against the four-point checklist, personalise the opening line, then decide whether to send.', approximateEffortMinutes: effort(a) },
        measurement: {
          mainObservation: 'Whether the conversation is relevant, or actual access is offered.',
          window: '14 days, allowing for slow replies.',
          limitation: 'Listener response is a separate question and only exists if the introduction actually happens.',
        },
        decisionRule: {
          repeatWhen: 'You get a relevant reply of any kind. Send two more with the same structure.',
          adjustWhen: 'Five well-matched, correctly-followed submissions get no reply. Change the contribution you are offering.',
          insufficientInformationWhen: 'You could not find a recipient that passes the checklist. That is a research step, not a failure.',
        },
        whatCanWait: { activity: 'Building a large outreach list.', reason: 'One checked, well-matched approach tells you more than twenty generic ones.' },
        missingInputs: missing(a, 'music_description', 'existing_access'),
      };
    },
  },

  D03: {
    id: 'D03',
    name: 'Focus an unsustainable spread of routes',
    category: 'DISCOVERY',
    coreHeadline: 'GIVE NEW LISTENERS A WAY IN.',
    problemAddressed: 'Several routes are running at once with too little time to execute or compare any of them.',
    triggerPatterns: ['discovery = SCATTERED', 'capacity = TINY or SMALL', 'learning = RANDOM or CHANGE_EVERYTHING'],
    gatesRequired: [
      'SCATTERED reported',
      'A confirmed capacity or comparison problem, not simply the use of more than one channel',
    ],
    assetProduced: 'One route for 14 days, the assets to reuse, a small schedule and a parking list.',
    mustNotClaim: [
      'A working multichannel approach is not abandoned on this answer alone',
      'No claim that the chosen platform is universally superior',
    ],
    blockingCases: ['Multichannel with adequate time and clear comparisons', 'No shareable music (B02)'],
    personalisation: personalisationFields.DISCOVERY,
    build: (a) => {
      const route = p(a, 'route_choice', 'OWN_POSTS');
      const routeText = selectLabel('DISCOVERY', 'route_choice', route);
      const music = p(a, 'music_description', 'the music you described');
      return {
        headline: 'RUN ONE ROUTE FOR FOURTEEN DAYS INSTEAD OF FOUR AT ONCE.',
        startHereLabel: 'The Discovery Fix',
        startHereBody: `You are trying several routes without enough time to learn from any of them. The next two weeks make one of them repeatable.`,
        basis: [
          { answerRef: 'Q3 discovery', paraphrase: optionLabel('discovery', 'SCATTERED'), basisType: 'ANSWER_PATTERN' },
          { answerRef: 'Q9 capacity', paraphrase: optionLabel('capacity', a.capacity ?? 'SMALL'), basisType: 'ANSWER_PATTERN' },
        ],
        asset: {
          title: 'Your fourteen-day route plan',
          copyButtonLabel: 'COPY MY OUTREACH DRAFT',
          readiness: 'READY_TO_USE',
          blocks: [
            { heading: 'The one route', body: `${routeText}. Chosen because you named it as realistic and it fits the time you have.` },
            { heading: 'Assets you reuse (make once)', body: `One excerpt of ${songName(a)}, one line describing it as ${music}, one image. Every attempt in the next two weeks uses these three things.` },
            { heading: 'Schedule inside your time', body: `Week 1: prepare the asset and make two attempts.\nWeek 2: two more attempts, same asset, same route.\nTotal: about ${effort(a)} minutes a week, including review.` },
            { heading: 'Parking list (not abandoned, just paused)', body: 'Every other route you mentioned goes here. Pick them up again after 14 days, when you have something to compare against.' },
            { heading: 'What you write down each time', body: 'Date, route, what you sent or posted, and any reply that mentioned the music.' },
          ],
          prerequisiteChecks: ['Make the reusable asset before day one, or the schedule will not hold.'],
        },
        firstAction: { instruction: 'Prepare the single reusable asset. Nothing else this week.', approximateEffortMinutes: effort(a) },
        measurement: {
          mainObservation: 'Whether you executed the four attempts, and whether any introduced the music to unfamiliar people.',
          window: '14 days.',
          limitation: 'Workload and effectiveness are separate. Finishing the plan does not mean the route works.',
        },
        decisionRule: {
          repeatWhen: 'You completed the attempts and saw any relevant introductions. Run the same route for another 14 days.',
          adjustWhen: 'You completed them and saw nothing relevant. Take the next route off the parking list, same asset.',
          insufficientInformationWhen: 'You could not complete the attempts. Halve the plan before changing route.',
        },
        whatCanWait: { activity: 'Adding another platform or channel.', reason: 'You already have more routes than time. Another one makes comparison harder, not easier.' },
        missingInputs: missing(a, 'music_description'),
      };
    },
  },

  // ================= CONTENT =================
  C01: {
    id: 'C01',
    name: 'Announcement-led to music-led',
    category: 'CONTENT',
    coreHeadline: 'LET PEOPLE EXPERIENCE THE SONG SOONER.',
    problemAddressed: 'Recent promotion is mostly artwork and release announcements, so people are told about the song rather than hearing it.',
    triggerPatterns: ['introduction includes ANNOUNCEMENTS', 'introduction does not include SONG_LED'],
    gatesRequired: [
      'Shareable music exists',
      'An online content route is actually in use (not NOT_ONLINE)',
      'Enough contextual detail to propose the alternative',
      'No confirmed destination problem taking priority',
    ],
    assetProduced: 'Opening shot, the supplied musical moment, on-screen words, a short filming sequence, caption and a release-appropriate next action.',
    mustNotClaim: [
      'Announcements are not blamed for slow growth',
      'No promise that the new post converts viewers into fans',
      'No invented lyric, timestamp or description of what the audio sounds like',
    ],
    blockingCases: [
      'NOT_ONLINE with a functioning offline route',
      'Confirmed broken destination (N01 first)',
      'No shareable music (B02)',
    ],
    personalisation: personalisationFields.CONTENT,
    build: (a) => {
      const moment = p(a, 'first_moment');
      const format = p(a, 'format_comfort', 'UNSURE');
      const released = a.readiness === 'RELEASED';
      return {
        headline: 'PUT THE SONG IN FRONT OF PEOPLE, NOT ANOTHER RELEASE GRAPHIC.',
        startHereLabel: 'The Content Fix',
        startHereBody: `Your recent posts have mainly announced that ${songName(a)} exists. The next thing to test is a post where someone hears it immediately.`,
        provisionalNote: 'This does not prove your announcement posts caused slow growth. It gives you a specific alternative worth trying.',
        basis: [
          { answerRef: 'Q4 introduction', paraphrase: optionLabel('introduction', 'ANNOUNCEMENTS'), basisType: 'ANSWER_PATTERN' },
          {
            answerRef: 'Q5 response',
            paraphrase: a.response.length ? optionLabel('response', a.response[0]) : 'No response reported.',
            basisType: 'ANSWER_PATTERN',
          },
        ],
        asset: {
          title: 'Your content brief',
          copyButtonLabel: 'COPY MY CONTENT BRIEF',
          readiness: 'READY_TO_USE',
          blocks: [
            { heading: 'What to film or assemble', body: filmingLine(format) },
            {
              heading: 'The moment to open on',
              body: moment
                ? `Start on: ${moment}\nNo build-up, no talking intro. The audio starts in the first second.`
                : 'You did not name a moment, so use the first part of the chorus. The rule is that the audio starts in the first second, with no spoken introduction.',
            },
            { heading: 'Text on screen', body: moment ? moment : `From ${songName(a)}.` },
            {
              heading: 'Caption',
              body: released
                ? `${songName(a)} is out now. The full song is through the link on my profile.`
                : `This is from ${songName(a)}. It is not out yet, so there is nothing to stream today. Follow if you want to know when it is.`,
            },
            { heading: 'Link label', body: released ? `Listen to ${songName(a)}` : 'Not applicable yet. Do not link to a destination where the song is not available.' },
            { heading: 'Before posting', body: 'Open your own profile as a stranger would and check the song is findable in two taps. We have not checked it for you.' },
          ],
          prerequisiteChecks: ['Use audio you already have. No new recording required.'],
        },
        firstAction: {
          instruction: `Make the simplest version with your phone and the existing audio. Keep it inside about ${effort(a)} minutes rather than turning it into a video project.`,
          approximateEffortMinutes: effort(a),
        },
        measurement: {
          mainObservation: 'Comments or messages that refer to the song itself, plus any observable action towards listening.',
          window: '7 days, compared with a similar earlier post over the same 7 days.',
          limitation: 'More views alone do not show the introduction created more listeners.',
        },
        decisionRule: {
          repeatWhen: 'You get any music-specific response. Make one more with the same structure and a different moment.',
          adjustWhen: 'Reach was similar to before but nothing about the music. Change the opening second, keep everything else.',
          insufficientInformationWhen: 'Almost nobody saw it. The next question is access to relevant listeners, not whether the song is good enough.',
        },
        whatCanWait: {
          activity: 'A complete rebrand or a paid campaign.',
          reason: 'Your answers point at how the song is introduced, not at your identity or your reach budget.',
        },
        missingInputs: missing(a, 'first_moment'),
      };
    },
  },

  C02: {
    id: 'C02',
    name: 'Connect attention back to the music',
    category: 'CONTENT',
    coreHeadline: 'LET PEOPLE EXPERIENCE THE SONG SOONER.',
    problemAddressed: 'The content people respond to is largely separate from the music, so attention does not lead anywhere musical.',
    triggerPatterns: ['introduction includes OTHER_CONTENT', 'response includes LIGHT_ATTENTION'],
    gatesRequired: [
      'A supplied example confirming the response is about the non-musical material',
      'Shareable music exists',
    ],
    assetProduced: 'A bridge from the successful subject into a specific moment in the song, a music-first comparison version, caption and one next action.',
    mustNotClaim: [
      'Viewers of the other content are not assumed to be listeners',
      'No invented trauma, controversy or topical link',
      'More views do not establish better musical fit',
    ],
    blockingCases: ['No real relationship between the content and the song (use C01 instead)', 'No shareable music (B02)'],
    personalisation: personalisationFields.CONTENT,
    build: (a) => {
      const moment = p(a, 'first_moment');
      const caption = p(a, 'recent_caption');
      const format = p(a, 'format_comfort', 'UNSURE');
      return {
        headline: 'BUILD ONE BRIDGE FROM THE THING PEOPLE WATCH TO THE THING YOU MADE.',
        startHereLabel: 'The Content Fix',
        startHereBody: `People respond to content that sits apart from your music. The test is whether that interest can lead into ${songName(a)} naturally, rather than having a streaming request bolted onto it.`,
        basis: [
          { answerRef: 'Q4 introduction', paraphrase: optionLabel('introduction', 'OTHER_CONTENT'), basisType: 'ANSWER_PATTERN' },
          { answerRef: 'Q5 response', paraphrase: optionLabel('response', 'LIGHT_ATTENTION'), basisType: 'ANSWER_PATTERN' },
        ],
        asset: {
          title: 'Your content brief',
          copyButtonLabel: 'COPY MY CONTENT BRIEF',
          readiness: 'READY_TO_USE',
          blocks: [
            { heading: 'Version 1: the bridge', body: `Open exactly as your usual content opens${caption ? ` (you described it as: ${caption})` : ''}. At the point where you would normally end, keep going into the music: ${moment || 'the part of the song you would most want a stranger to hear'}. Do not announce the change. Let the audio take over.` },
            { heading: 'How to shoot it', body: filmingLine(format) },
            { heading: 'Caption for the bridge version', body: `Same as usual, plus one line: the music underneath is mine. It is called ${songName(a)}.` },
            { heading: 'Version 2: the control', body: 'A music-only version of the same moment, with no connection to the other subject. Post it within a week of version 1 so you can compare like with like.' },
            { heading: 'If there is no real connection', body: 'Do not force one. Post version 2 on its own and treat the other content as a separate thing you enjoy making.' },
          ],
          prerequisiteChecks: ['The bridge must use a connection that genuinely exists. If you have to invent it, use version 2 only.'],
        },
        firstAction: { instruction: 'Make version 1 using footage or a format you already use.', approximateEffortMinutes: effort(a) },
        measurement: {
          mainObservation: 'Responses that mention the music, rather than only the original topic.',
          window: '7 days per version, same window for both.',
          limitation: 'Your usual content may get more reach for reasons unrelated to the music. Compare the type of response, not the totals.',
        },
        decisionRule: {
          repeatWhen: 'The bridge version gets any music-specific replies. Make a second one.',
          adjustWhen: 'Only the usual topic gets discussed. Make the music the opening rather than the ending.',
          insufficientInformationWhen: 'Both versions got very little reach this time.',
        },
        whatCanWait: { activity: 'Stopping the content that works.', reason: 'Nothing in your answers suggests it is hurting you. The question is only whether it can lead anywhere musical.' },
        missingInputs: missing(a, 'first_moment'),
      };
    },
  },

  C03: {
    id: 'C03',
    name: 'Make a format the artist can sustain',
    category: 'CONTENT',
    coreHeadline: 'LET PEOPLE EXPERIENCE THE SONG SOONER.',
    problemAddressed: 'Very little gets made because filming, editing or time make each attempt too expensive.',
    triggerPatterns: ['introduction includes VERY_LITTLE', 'capacity = TINY or SMALL', 'format comfort is limited'],
    gatesRequired: [
      'A content route is appropriate for this artist',
      'The obstacle is execution cost, not a working offline route',
      'Shareable music exists',
    ],
    assetProduced: 'One reusable format with a first instance ready to make, asset list, shot sequence, no-face option and a small batch plan.',
    mustNotClaim: [
      'No inference of laziness or lack of commitment',
      'No prescribed daily posting, face-to-camera or stock footage',
    ],
    blockingCases: ['NOT_ONLINE with a functioning offline route', 'No shareable music (B02)'],
    personalisation: personalisationFields.CONTENT,
    build: (a) => {
      const moment = p(a, 'first_moment');
      const format = p(a, 'format_comfort', 'UNSURE');
      const noFace = format === 'HANDS' || format === 'ARTWORK' || format === 'AUDIO';
      return {
        headline: 'ONE FORMAT YOU CAN MAKE IN ONE SITTING, FOUR TIMES.',
        startHereLabel: 'The Content Fix',
        startHereBody: `Very little has gone out recently, and the time you have is limited. The fix is a format cheap enough that making it is never the reason it does not happen.`,
        basis: [
          { answerRef: 'Q4 introduction', paraphrase: optionLabel('introduction', 'VERY_LITTLE'), basisType: 'ANSWER_PATTERN' },
          { answerRef: 'Q9 capacity', paraphrase: optionLabel('capacity', a.capacity ?? 'TINY'), basisType: 'ANSWER_PATTERN' },
        ],
        asset: {
          title: 'Your content brief',
          copyButtonLabel: 'COPY MY CONTENT BRIEF',
          readiness: 'READY_TO_USE',
          blocks: [
            { heading: 'The repeatable format', body: `One fixed frame, one musical moment, one line of text. ${noFace ? 'No face and no speaking at any point.' : 'You on camera only if you want to be.'} The frame never changes between instances, so setup happens once.` },
            { heading: 'Shot sequence', body: `1. ${filmingLine(format)}\n2. Start the audio in the first second.\n3. Hold for 15 to 20 seconds.\n4. Stop. No edit beyond a trim.` },
            { heading: 'First instance, ready to make', body: moment ? `Use: ${moment}` : `Use the opening of ${songName(a)}. Choose the moment now, before you pick up the phone.` },
            { heading: 'Asset list', body: 'Phone, the existing audio, one surface or background you can return to. Nothing bought, nothing borrowed.' },
            { heading: 'Batch plan inside your time', body: `Record three instances in one sitting of about ${effort(a)} minutes. Post them across two weeks. That is the whole commitment.` },
          ],
          prerequisiteChecks: ['Choose the fixed frame once. Changing it between instances is what makes this expensive again.'],
        },
        firstAction: { instruction: 'Record one instance today. Do not edit it beyond trimming the ends.', approximateEffortMinutes: effort(a) },
        measurement: {
          mainObservation: 'Whether you actually made three, and separately whether any drew song-specific responses.',
          window: '14 days.',
          limitation: 'Being easy to make says nothing about whether it works. Keep the two judgements apart.',
        },
        decisionRule: {
          repeatWhen: 'You made all three. Record the next batch with the same frame.',
          adjustWhen: 'You made them but saw nothing. Change the musical moment, keep the format.',
          insufficientInformationWhen: 'You could not make three. Cut it to one a fortnight rather than abandoning it.',
        },
        whatCanWait: { activity: 'A posting schedule, a content calendar or new equipment.', reason: 'None of those are the reason little has gone out. The cost per post is.' },
        missingInputs: missing(a, 'first_moment'),
      };
    },
  },

  // ================= NEXT STEP =================
  N01: {
    id: 'N01',
    name: 'Repair a missing or mismatched route',
    category: 'NEXT_STEP',
    coreHeadline: 'MAKE THE NEXT LISTEN EASY TO FIND.',
    problemAddressed: 'Interested people are sent somewhere the featured song is absent, wrong or hard to identify.',
    triggerPatterns: ['nextStep = NO_ROUTE', 'nextStep = UNKNOWN', 'artist confirms the destination does not surface the song'],
    gatesRequired: [
      'The artist confirms the destination problem; a link page alone is not evidence',
      'There is a destination that could be offered instead, or a concrete setup task',
    ],
    assetProduced: 'Before and after call to action, link label, a profile or pinned line, and the exact configuration change.',
    mustNotClaim: [
      'No claim that a URL was inspected',
      'No promise that fixing a link fixes growth',
      'No instruction to delete other useful destinations',
    ],
    blockingCases: ['Artist confirms the general link page clearly highlights the song', 'No shareable music (B02)'],
    personalisation: personalisationFields.NEXT_STEP,
    build: (a) => {
      const current = p(a, 'current_cta');
      const report = p(a, 'destination_report', 'the destination you described');
      const dest = p(a, 'available_destination', 'PROFILE');
      const destText = selectLabel('NEXT_STEP', 'available_destination', dest);
      const noneYet = dest === 'NONE';
      return {
        headline: 'FIX THE ROUTE BEFORE YOU SEND ANYONE ELSE DOWN IT.',
        startHereLabel: 'The Next-Step Fix',
        startHereBody: `You told us people end up at ${report}. Before spending anything on reach, make the thing they came for obvious when they arrive.`,
        basis: [
          { answerRef: 'Q6 next step', paraphrase: optionLabel('nextStep', a.nextStep ?? 'NO_ROUTE'), basisType: 'ANSWER_PATTERN' },
          { answerRef: 'Personalisation: destination', paraphrase: report, basisType: 'SPECIFIC_REPORTED_EXAMPLE' },
        ],
        asset: {
          title: 'Your next-step copy',
          copyButtonLabel: 'COPY MY NEXT-STEP COPY',
          readiness: noneYet ? 'NEEDS_RECIPIENT_OR_DESTINATION_CHECK' : 'READY_TO_USE',
          blocks: [
            { heading: 'Before (your words)', body: current || 'You did not paste your current wording, so compare the line below against whatever you currently use.' },
            {
              heading: 'After',
              body: noneYet
                ? `Nothing to link to yet. Set up ${a.readiness === 'RELEASED' ? 'a direct link to the released song' : 'one page that holds the song or a reminder'} first, then use: ${songName(a)} is here. [link]`
                : `${songName(a)} is here: [${destText.toLowerCase()}]. One tap, nothing else to find.`,
            },
            { heading: 'Link label', body: noneYet ? 'Set the destination up before writing a label.' : `Listen to ${songName(a)}` },
            { heading: 'Profile or pinned line', body: `${songName(a)} - start here.` },
            { heading: 'The exact change to make', body: noneYet ? `Create ${destText === 'None yet' ? 'the one destination you can maintain' : destText.toLowerCase()}. Until that exists, this draft cannot be used.` : `Put ${songName(a)} at the very top of ${report}, above everything else, and remove anything above it.` },
            { heading: 'Visitor check', body: 'Open the route on someone else\'s phone, logged out. Time how long it takes to start the song. Two taps or fewer.' },
          ],
          prerequisiteChecks: noneYet ? ['Create the destination before using any of this copy.'] : ['Check it logged out, not from your own account.'],
        },
        firstAction: { instruction: noneYet ? 'Set up the one destination you can actually maintain.' : 'Make the change, then check the route from a logged-out phone.', approximateEffortMinutes: Math.min(effort(a), 30) },
        measurement: {
          mainObservation: 'Whether the intended song is reachable in two taps, and whether questions about where to hear it stop.',
          window: '14 days after the change.',
          limitation: 'Any change in listening needs separate evidence. A fixed route does not prove more people listened.',
        },
        decisionRule: {
          repeatWhen: 'The route works and access questions stop. Move on to how you introduce the song.',
          adjustWhen: 'People still ask where to hear it. The label or its placement is still unclear.',
          insufficientInformationWhen: 'Too few people arrive at the route to tell.',
        },
        whatCanWait: { activity: 'Buying reach or remaking your content.', reason: 'Until the route works, extra attention arrives somewhere that loses it.' },
        missingInputs: missing(a, 'current_cta', 'destination_report'),
      };
    },
  },

  N02: {
    id: 'N02',
    name: 'Choose one request',
    category: 'NEXT_STEP',
    coreHeadline: 'MAKE THE NEXT LISTEN EASY TO FIND.',
    problemAddressed: 'Several competing requests are made at once, so no single action is obvious.',
    triggerPatterns: ['nextStep = MANY_ACTIONS', 'pasted copy contains several asks'],
    gatesRequired: ['MANY_ACTIONS reported, or supplied copy confirms competing requests'],
    assetProduced: 'One chosen action matched to the goal, a rewritten call to action, and a place for the other legitimate actions later.',
    mustNotClaim: ['Reduced choice is a test here, not a universal law', 'Necessary event access details are never removed'],
    blockingCases: ['A single clear request already in use'],
    personalisation: personalisationFields.NEXT_STEP,
    build: (a) => {
      const current = p(a, 'current_cta');
      const dest = p(a, 'available_destination', 'PROFILE');
      const destText = selectLabel('NEXT_STEP', 'available_destination', dest);
      const chosen =
        a.goal === 'LOCAL_SHOW'
          ? 'come to the show'
          : a.goal === 'MORE_RETURN'
          ? 'hear the second song'
          : a.readiness === 'RELEASED'
          ? `listen to ${songName(a)}`
          : 'get told when it is out';
      return {
        headline: 'ASK FOR ONE THING, IN THE PLACE THAT DELIVERS IT.',
        startHereLabel: 'The Next-Step Fix',
        startHereBody: `Your current wording asks for several things at once. For this test, give one clear next move and make the destination match it.`,
        basis: [
          { answerRef: 'Q6 next step', paraphrase: optionLabel('nextStep', 'MANY_ACTIONS'), basisType: 'ANSWER_PATTERN' },
          { answerRef: 'Q1 goal', paraphrase: optionLabel('goal', a.goal ?? 'MORE_DISCOVERY'), basisType: 'ANSWER_PATTERN' },
        ],
        asset: {
          title: 'Your next-step copy',
          copyButtonLabel: 'COPY MY NEXT-STEP COPY',
          readiness: 'READY_TO_USE',
          blocks: [
            { heading: 'Before (your words)', body: current || 'You did not paste your wording. Compare this against whatever you currently ask for.' },
            { heading: 'The one action for this test', body: `${chosen}. Chosen because your stated goal is ${goalPhrase(a)}.` },
            { heading: 'After', body: `${songName(a)} - ${chosen}. [${destText.toLowerCase()}]` },
            { heading: 'Where the other requests go', body: 'Following, sharing and signing up are all legitimate. They belong in your profile and in later posts, not in the same sentence as this one.' },
            { heading: 'What stays regardless', body: a.goal === 'LOCAL_SHOW' ? 'Date, venue, time and ticket details stay in the post. Those are information, not requests.' : 'Anything factual someone needs in order to act stays.' },
          ],
          prerequisiteChecks: ['Check the destination actually delivers the single action you are now asking for.'],
        },
        firstAction: { instruction: 'Replace the multi-request wording on the next thing you post or send.', approximateEffortMinutes: Math.min(effort(a), 20) },
        measurement: {
          mainObservation: 'Completion of the one requested action, where you can see it, and whether the destination still matches.',
          window: '14 days.',
          limitation: 'Fewer asks is a test for your situation, not a rule that always increases response.',
        },
        decisionRule: {
          repeatWhen: 'The single ask gets acted on more clearly. Use it for the next three posts.',
          adjustWhen: 'Nothing changes. Try a different single action from the list you parked.',
          insufficientInformationWhen: 'You cannot observe the action at all. Choose one you can see.',
        },
        whatCanWait: { activity: 'Building a mailing list or a fan community.', reason: 'Both are real, but adding them now recreates the competing-requests problem you are testing.' },
        missingInputs: missing(a, 'current_cta'),
      };
    },
  },

  N03: {
    id: 'N03',
    name: 'Match the action to release status',
    category: 'NEXT_STEP',
    coreHeadline: 'MAKE THE NEXT LISTEN EASY TO FIND.',
    problemAddressed: 'The promotion asks for something that is not available yet, because the song is unreleased.',
    triggerPatterns: ['nextStep = UNRELEASED_UNCLEAR', 'readiness = UNRELEASED_SHAREABLE with a listening request'],
    gatesRequired: ['Confirmed unreleased status', 'Confirmation that there is no usable action, not merely the NEXT_RELEASE goal'],
    assetProduced: 'Copy for an action available today, kept separate from the future release instruction.',
    mustNotClaim: [
      'No invented pre-save link, reminder feature, release date, signup page or event',
      'No fictional streams from unreleased music',
    ],
    blockingCases: ['Released song with a working destination'],
    personalisation: personalisationFields.NEXT_STEP,
    build: (a) => {
      const dest = p(a, 'available_destination', 'NONE');
      const destText = selectLabel('NEXT_STEP', 'available_destination', dest);
      const nothing = dest === 'NONE';
      return {
        headline: 'ASK FOR SOMETHING THEY CAN ACTUALLY DO TODAY.',
        startHereLabel: 'The Next-Step Fix',
        startHereBody: `${songName(a)} is not available where your promotion would send people. Use an action that exists now, and change the invitation when the song is out.`,
        basis: [
          { answerRef: 'Q1 readiness', paraphrase: optionLabel('readiness', a.readiness ?? 'UNRELEASED_SHAREABLE'), basisType: 'ANSWER_PATTERN' },
          { answerRef: 'Q6 next step', paraphrase: optionLabel('nextStep', a.nextStep ?? 'UNRELEASED_UNCLEAR'), basisType: 'ANSWER_PATTERN' },
        ],
        asset: {
          title: 'Your next-step copy',
          copyButtonLabel: 'COPY MY NEXT-STEP COPY',
          readiness: nothing ? 'NEEDS_RECIPIENT_OR_DESTINATION_CHECK' : 'READY_TO_USE',
          blocks: [
            {
              heading: 'Available now',
              body: nothing
                ? `You told us no destination exists yet. Set up one thing you can maintain before promoting: a profile that holds your existing music, or a way for people to be told when ${songName(a)} is out. Until then there is no honest ask to make.`
                : `${destText}. Use it as the only action: "${songName(a)} is not out yet. ${dest === 'REMINDER' ? 'This is where you can be told when it is' : 'This is what there is to hear in the meantime'}: [${destText.toLowerCase()}]."`,
            },
            { heading: 'For the release itself, later', body: `Keep this separate and unused until the song is actually live: "${songName(a)} is out. [link]" Do not post it early.` },
            { heading: 'What not to write', body: 'No pre-save link, no reminder button, no release date and no signup page unless the thing already exists and you have set it up yourself.' },
            { heading: 'What you can honestly say today', body: `There is an excerpt of ${songName(a)} you can share. That is real. Share it without asking anyone to stream anything.` },
          ],
          prerequisiteChecks: nothing ? ['Create one real destination before using any of this.'] : ['Confirm the destination you named exists and is public.'],
        },
        firstAction: { instruction: nothing ? 'Set up the one destination you can maintain.' : 'Replace any "stream it" wording with the available action above.', approximateEffortMinutes: Math.min(effort(a), 30) },
        measurement: {
          mainObservation: 'Actual use of that specific available action.',
          window: 'Until release, then reset.',
          limitation: 'An unreleased song has no streams. Nothing here should be judged by listening numbers yet.',
        },
        decisionRule: {
          repeatWhen: 'People use the available action. Keep it until release day.',
          adjustWhen: 'Nobody uses it. The excerpt, not the ask, is the next thing to look at.',
          insufficientInformationWhen: 'There is no destination yet, so nothing is measurable.',
        },
        whatCanWait: { activity: 'Release-day promotion planning.', reason: 'There is no usable action today. Plan the day once something exists to point at.' },
        missingInputs: missing(a, 'available_destination'),
      };
    },
  },

  // ================= RETURN =================
  R01: {
    id: 'R01',
    name: 'Continue beyond the launch',
    category: 'RETURN',
    coreHeadline: 'GIVE INTERESTED LISTENERS A REASON TO COME BACK.',
    problemAddressed: 'People showed real interest, then promotion stopped or moved on.',
    triggerPatterns: ['response includes SONG_ENQUIRIES or DELIBERATE_INTEREST', 'return = MOVE_ON or NO_FOLLOW_UP'],
    gatesRequired: [
      'Reported song-specific interest exists',
      'Available material for a second encounter',
      'No confirmed destination problem taking priority',
    ],
    assetProduced: 'A second encounter using existing material, exact copy, a small sequence and one reason to return.',
    mustNotClaim: [
      'Poor retention is not concluded from missing repeat-listener analytics',
      'Anonymous views cannot establish which individuals returned',
      'No schedule the artist has not agreed to maintain',
    ],
    blockingCases: ['No reported interest at all (investigate access instead)', 'Confirmed broken destination (N01)'],
    personalisation: personalisationFields.RETURN,
    build: (a) => {
      const responded = p(a, 'responded_song', 'the song you said people responded to');
      const next = p(a, 'available_next', 'ANOTHER_SECTION');
      const nextText = selectLabel('RETURN', 'available_next', next);
      const fmt = p(a, 'format_preference', 'NO_PREFERENCE');
      return {
        headline: 'GIVE THIS SONG A SECOND ENCOUNTER, NOT A SECOND ANNOUNCEMENT.',
        startHereLabel: 'The Return Fix',
        startHereBody: `People showed real interest in ${responded}, and then the promotion moved on. The first test is one more meaningful encounter with the same music.`,
        basis: [
          { answerRef: 'Q5 response', paraphrase: responded, basisType: 'SPECIFIC_REPORTED_EXAMPLE' },
          { answerRef: 'Q7 return', paraphrase: optionLabel('return', a.return ?? 'MOVE_ON'), basisType: 'ANSWER_PATTERN' },
        ],
        asset: {
          title: 'Your follow-up plan',
          copyButtonLabel: 'COPY MY FOLLOW-UP PLAN',
          readiness: next === 'NONE' ? 'NEEDS_RECIPIENT_OR_DESTINATION_CHECK' : 'READY_TO_USE',
          blocks: [
            { heading: 'The second encounter', body: next === 'NONE' ? 'You said there is nothing available yet. The smallest honest option is a different part of the same recording, which needs no new work.' : `${nextText}, using material that already exists.${fmt === 'NO_FACE' ? ' No face on camera at any point.' : fmt === 'AUDIO_ONLY' ? ' Audio only, over one still image.' : ''}` },
            { heading: 'Copy to use', body: `A few of you asked about ${responded}. Here is the part I did not put out.` },
            { heading: 'The reason to come back', body: 'It is more of the thing they already chose, not a repeat of the request to support it.' },
            { heading: 'Small proposed sequence', body: `Now: the second encounter above.\nAbout ten days later: one more, if the first gets any response.\nThat is the whole plan. It is a suggestion, not a schedule you have committed to.` },
            { heading: 'What this will not tell you', body: 'Views cannot show which specific people came back. Reported replies from people you recognise are the more useful signal.' },
          ],
          prerequisiteChecks: ['Use material that already exists. Nothing here requires a new recording.'],
        },
        firstAction: { instruction: 'Prepare the one follow-up piece from material you already have.', approximateEffortMinutes: effort(a) },
        measurement: {
          mainObservation: 'Replies from people who responded the first time, and any further song-specific comment.',
          window: '14 days.',
          limitation: 'You cannot attribute anonymous views to returning listeners.',
        },
        decisionRule: {
          repeatWhen: 'Anyone who responded before responds again. Do the second piece in the sequence.',
          adjustWhen: 'Silence from people who previously engaged. Change what the follow-up offers, not how often you ask.',
          insufficientInformationWhen: 'You cannot tell who responded either time.',
        },
        whatCanWait: { activity: 'Starting the next release.', reason: 'There is interest in this one that has not been followed up. A new song restarts from zero.' },
        missingInputs: missing(a, 'responded_song'),
      };
    },
  },

  R02: {
    id: 'R02',
    name: 'Offer something beyond the same request',
    category: 'RETURN',
    coreHeadline: 'GIVE INTERESTED LISTENERS A REASON TO COME BACK.',
    problemAddressed: 'Follow-up exists but repeats the same request rather than offering a new encounter.',
    triggerPatterns: ['return = SAME_REQUEST', 'response includes SONG_ENQUIRIES or DELIBERATE_INTEREST'],
    gatesRequired: ['Actual initial musical interest reported', 'A feasible different angle available'],
    assetProduced: 'One follow-up giving a different musical experience or genuine context, with copy that makes its relevance clear.',
    mustNotClaim: ['No forced story where another musical passage would do', 'No guilt-based asks'],
    blockingCases: ['No reported interest', 'No available second angle at all'],
    personalisation: personalisationFields.RETURN,
    build: (a) => {
      const responded = p(a, 'responded_song', 'the song people responded to');
      const next = p(a, 'available_next', 'ANOTHER_SECTION');
      const nextText = selectLabel('RETURN', 'available_next', next);
      return {
        headline: 'SAY SOMETHING NEW INSTEAD OF ASKING AGAIN.',
        startHereLabel: 'The Return Fix',
        startHereBody: `You are following up, but the message is essentially the same request. The test is a second piece that gives someone something different to hear.`,
        basis: [
          { answerRef: 'Q7 return', paraphrase: optionLabel('return', 'SAME_REQUEST'), basisType: 'ANSWER_PATTERN' },
          { answerRef: 'Personalisation: response', paraphrase: responded, basisType: 'SPECIFIC_REPORTED_EXAMPLE' },
        ],
        asset: {
          title: 'Your follow-up plan',
          copyButtonLabel: 'COPY MY FOLLOW-UP PLAN',
          readiness: 'READY_TO_USE',
          blocks: [
            { heading: 'Replace this', body: 'Any version of "please listen", "still out now" or "it would mean a lot". Those repeat the ask without adding anything.' },
            { heading: 'With this', body: `${nextText} from ${responded}. Something they have not heard, presented on its own terms.` },
            { heading: 'Copy to use', body: `This is the other half of ${responded}. No ask attached.` },
            { heading: 'Why it is different', body: 'The first encounter asked for something. This one gives something. That is the whole change being tested.' },
          ],
          prerequisiteChecks: ['Use existing material. If nothing exists, a different section of the same recording is enough.'],
        },
        firstAction: { instruction: 'Make the one follow-up piece, with no request attached.', approximateEffortMinutes: effort(a) },
        measurement: {
          mainObservation: 'Responses to the music or the added perspective, rather than the number of times you asked.',
          window: '14 days.',
          limitation: 'People who already engage with you are not a general audience.',
        },
        decisionRule: {
          repeatWhen: 'The no-ask piece gets more musical response than the repeated request did.',
          adjustWhen: 'Neither gets a response. The question moves to who is seeing it at all.',
          insufficientInformationWhen: 'Reach differed so much between the two that comparison is meaningless.',
        },
        whatCanWait: { activity: 'Posting the same request more often.', reason: 'Frequency is not the variable your answers point at. Content of the follow-up is.' },
        missingInputs: missing(a, 'responded_song'),
      };
    },
  },

  R03: {
    id: 'R03',
    name: 'Bridge to related available music or a real next event',
    category: 'RETURN',
    coreHeadline: 'GIVE INTERESTED LISTENERS A REASON TO COME BACK.',
    problemAddressed: 'Interest exists and a related song, performance or event exists, but nothing connects them.',
    triggerPatterns: ['response includes DELIBERATE_INTEREST or RETURNING_INTEREST', 'a related item is supplied'],
    gatesRequired: ['Reported interest', 'A supplied related item and its relationship to the first'],
    assetProduced: 'A natural transition from the entry song to the next available item, with exact copy and the musical reason.',
    mustNotClaim: ['No demand for a newsletter or membership community', 'No invented related item'],
    blockingCases: ['No related item supplied (use R01 or R02)'],
    personalisation: personalisationFields.RETURN,
    build: (a) => {
      const responded = p(a, 'responded_song', 'the song people responded to');
      const next = p(a, 'available_next', 'ANOTHER_SONG');
      const nextText = selectLabel('RETURN', 'available_next', next);
      const isEvent = next === 'CONFIRMED_EVENT';
      return {
        headline: 'CONNECT THE SONG THEY CHOSE TO THE THING THAT COMES NEXT.',
        startHereLabel: 'The Return Fix',
        startHereBody: `People are choosing to hear ${responded}, and you have ${nextText.toLowerCase()} available. Make the link between them explicit.`,
        basis: [
          { answerRef: 'Q5 response', paraphrase: responded, basisType: 'SPECIFIC_REPORTED_EXAMPLE' },
          { answerRef: 'Personalisation: available next', paraphrase: nextText, basisType: 'SPECIFIC_REPORTED_EXAMPLE' },
        ],
        asset: {
          title: 'Your follow-up plan',
          copyButtonLabel: 'COPY MY FOLLOW-UP PLAN',
          readiness: isEvent ? 'NEEDS_RECIPIENT_OR_DESTINATION_CHECK' : 'READY_TO_USE',
          blocks: [
            { heading: 'The bridge', body: `Open with the part of ${responded} people already respond to, then move straight into ${nextText.toLowerCase()}. No announcement between them.` },
            { heading: 'Copy to use', body: `If ${responded} was your thing, this is where it goes next.` },
            { heading: 'Why the connection may matter musically', body: 'You described these as related. State that relationship in your own words in one line, rather than leaving the listener to guess.' },
            { heading: isEvent ? 'Event details that must stay' : 'Where to put it', body: isEvent ? 'Date, venue, time and ticket route stay in the post. Confirm they are correct before posting.' : 'Use it as the follow-up post and as the second line of your profile.' },
          ],
          prerequisiteChecks: isEvent ? ['Confirm the event details yourself. We have not checked anything.'] : ['Confirm the related item is actually available to hear.'],
        },
        firstAction: { instruction: 'Add the bridge to your next follow-up post or to the destination people already reach.', approximateEffortMinutes: effort(a) },
        measurement: {
          mainObservation: 'Chosen engagement with the second item where you can observe it.',
          window: '14 days.',
          limitation: 'You will not be able to attribute most of it precisely. Reported responses are worth more than totals here.',
        },
        decisionRule: {
          repeatWhen: 'Anyone follows the bridge. Use the same structure for the next pair.',
          adjustWhen: 'Nobody moves across. The connection may be clear to you and not to a listener. Say it more plainly.',
          insufficientInformationWhen: 'You have no way to see whether anyone moved between the two.',
        },
        whatCanWait: { activity: 'Starting an email list or a fan community.', reason: 'You already have a related item people can move to. Build the habit before building infrastructure.' },
        missingInputs: missing(a, 'responded_song', 'available_next'),
      };
    },
  },

  // ================= TESTING =================
  T01: {
    id: 'T01',
    name: 'Stop changing every variable together',
    category: 'TESTING',
    coreHeadline: 'MAKE YOUR NEXT ATTEMPT TELL YOU SOMETHING.',
    problemAddressed: 'Several things change between attempts, so no attempt can be interpreted.',
    triggerPatterns: ['learning = CHANGE_EVERYTHING', 'real attempts exist'],
    gatesRequired: ['Actual attempts have been made', 'Something comparable can be held constant'],
    assetProduced: 'Two executable versions of one small test, the single difference, the constants, observation windows and a log.',
    mustNotClaim: ['Two organic posts are not a controlled experiment', 'No universal numeric success threshold'],
    blockingCases: ['learning = TOO_EARLY with no attempts (use B01)'],
    personalisation: personalisationFields.TESTING,
    build: (a) => {
      const activity = p(a, 'activity_under_review', 'the activity you described');
      const obs = p(a, 'available_observations', 'NONE');
      const obsText = selectLabel('TESTING', 'available_observations', obs);
      return {
        headline: 'CHANGE ONE THING BETWEEN THE NEXT TWO ATTEMPTS.',
        startHereLabel: 'The Testing Fix',
        startHereBody: `Several things change between your attempts, so none of them can tell you much. The next two hold everything steady except one difference.`,
        basis: [
          { answerRef: 'Q8 learning', paraphrase: optionLabel('learning', 'CHANGE_EVERYTHING'), basisType: 'ANSWER_PATTERN' },
          { answerRef: 'Personalisation: activity', paraphrase: activity, basisType: 'SPECIFIC_REPORTED_EXAMPLE' },
        ],
        asset: {
          title: 'Your test plan',
          copyButtonLabel: 'COPY MY TEST PLAN',
          readiness: 'READY_TO_USE',
          blocks: [
            { heading: 'The question', body: `Does changing the opening of ${activity} change the kind of response, rather than the size of it?` },
            { heading: 'Version A', body: `${activity}, exactly as you would normally do it.` },
            { heading: 'Version B', body: `The same thing, with one difference: the opening. Same excerpt of ${songName(a)}, same length, same day of week, same caption structure.` },
            { heading: 'What stays identical', body: 'Excerpt, length, destination, time of day, amount of effort. If you change two things, the test tells you nothing.' },
            { heading: 'Observation window', body: '7 days from posting for each. Do not compare a 7-day-old post with a 2-day-old one.' },
            { heading: 'Your log (four columns)', body: 'Date | Version | What was different | Music-specific responses\n\nWrite the last column as a count and a quote, not an impression.' },
          ],
          prerequisiteChecks: ['Decide the single difference before making either version.'],
        },
        firstAction: { instruction: 'Prepare version A and version B now, so the difference is deliberate rather than accidental.', approximateEffortMinutes: effort(a) },
        measurement: {
          mainObservation: obs === 'NONE' ? 'Counted replies that mention the music. That needs nothing but your own eyes.' : `${obsText}, plus counted music-specific replies.`,
          window: '7 days each, compared over equal windows.',
          limitation: 'Reach differs between organic posts, so this shows a direction, not a cause.',
        },
        decisionRule: {
          repeatWhen: 'One version clearly draws more music-specific response. Run it again against a third version.',
          adjustWhen: 'They are indistinguishable. The opening is not the variable that matters here.',
          insufficientInformationWhen: 'One version got a fraction of the reach. Repeat before drawing anything from it.',
        },
        whatCanWait: { activity: 'Rethinking the strategy as a whole.', reason: 'You cannot evaluate a strategy while every attempt inside it is different.' },
        missingInputs: missing(a, 'activity_under_review'),
      };
    },
  },

  T02: {
    id: 'T02',
    name: 'Measure closer to the intended action',
    category: 'TESTING',
    coreHeadline: 'MAKE YOUR NEXT ATTEMPT TELL YOU SOMETHING.',
    problemAddressed: 'Decisions are made on views and likes, which sit a long way from the actual goal.',
    triggerPatterns: ['learning = VIEWS_ONLY', 'goal is listener interest or return'],
    gatesRequired: ['Real attempts exist', 'A closer observation is actually accessible'],
    assetProduced: 'One main accessible action to record, its limitation and a revised log.',
    mustNotClaim: ['A click is not a stream', 'No added metrics counted as fans', 'No invented cost per fan'],
    blockingCases: ['No attempts at all (use B01)'],
    personalisation: personalisationFields.TESTING,
    build: (a) => {
      const activity = p(a, 'activity_under_review', 'the activity you described');
      const obs = p(a, 'available_observations', 'REPLIES');
      const obsText = selectLabel('TESTING', 'available_observations', obs);
      return {
        headline: 'RECORD SOMETHING CLOSER TO WHAT YOU ACTUALLY WANT.',
        startHereLabel: 'The Testing Fix',
        startHereBody: `You are choosing what to repeat based on views. Your goal is ${goalPhrase(a)}, which views sit a long way from. Add one observation closer to it.`,
        basis: [
          { answerRef: 'Q8 learning', paraphrase: optionLabel('learning', 'VIEWS_ONLY'), basisType: 'ANSWER_PATTERN' },
          { answerRef: 'Q1 goal', paraphrase: optionLabel('goal', a.goal ?? 'MORE_DISCOVERY'), basisType: 'ANSWER_PATTERN' },
        ],
        asset: {
          title: 'Your test plan',
          copyButtonLabel: 'COPY MY TEST PLAN',
          readiness: 'READY_TO_USE',
          blocks: [
            { heading: 'The signal to start recording', body: `${obsText}, taken from ${activity}.` },
            { heading: 'Its honest limitation', body: obs === 'CLICKS' ? 'A click is someone arriving, not someone listening. Never report clicks as streams.' : obs === 'STREAMING' ? 'Aggregate streaming data cannot be attributed to a single post. Treat it as context.' : obs === 'POST_DATA' ? 'Post data describes distribution, not musical interest.' : 'Counted replies are small numbers, but they are the ones that actually mention the music.' },
            { heading: 'Views stay in the log', body: 'Keep recording views as context for how many people had the chance to respond. Just stop deciding with them alone.' },
            { heading: 'Revised log', body: `Date | Activity | Views (context) | ${obsText} | Music-specific replies (count + quote)` },
            { heading: 'What not to add together', body: 'Views, clicks and streams are different events. Never sum them into a single figure, and never call any of them fans.' },
          ],
          prerequisiteChecks: ['Check you can actually access the chosen observation before relying on it.'],
        },
        firstAction: { instruction: 'Start the revised log on your next activity.', approximateEffortMinutes: Math.min(effort(a), 30) },
        measurement: {
          mainObservation: `${obsText} recorded per attempt, alongside counted music-specific replies.`,
          window: '7 days per attempt, three attempts before reading anything into it.',
          limitation: 'Where you have no reliable denominator, record raw counts. Do not invent a percentage.',
        },
        decisionRule: {
          repeatWhen: 'The closer signal moves in line with the effort. Keep that activity.',
          adjustWhen: 'Views are high and the closer signal is flat. That is the finding, and it points at the introduction.',
          insufficientInformationWhen: 'Fewer than three comparable attempts so far.',
        },
        whatCanWait: { activity: 'Chasing a higher view count.', reason: 'You already get views. The gap your answers describe is between views and anything musical.' },
        missingInputs: missing(a, 'activity_under_review'),
      };
    },
  },

  T03: {
    id: 'T03',
    name: 'Check a promising pattern before expanding',
    category: 'TESTING',
    coreHeadline: 'MAKE YOUR NEXT ATTEMPT TELL YOU SOMETHING.',
    problemAddressed: 'One attempt went well and is being built on without any check that it repeats.',
    triggerPatterns: ['learning = REPEAT_FEELING', 'a supplied isolated success', 'real attempts but no comparable repeat'],
    gatesRequired: ['Real attempts exist', 'A promising attempt that can be repeated closely'],
    assetProduced: 'A repeatability plan preserving the promising features, an observation window and a decision.',
    mustNotClaim: ['No inferred scalable return, budget recommendation or trend from a small sample'],
    blockingCases: ['No attempts (use B01)', 'learning = COMPARE with otherwise coherent answers (use NO_CLEAR_GAP)'],
    personalisation: personalisationFields.TESTING,
    build: (a) => {
      const activity = p(a, 'activity_under_review', 'the attempt you described');
      const pair = p(a, 'attempt_pair');
      return {
        headline: 'REPEAT THE ONE THAT WORKED BEFORE BUILDING ON IT.',
        startHereLabel: 'The Testing Fix',
        startHereBody: `There is an attempt in what you described worth revisiting. Before changing the plan or spending anything, check whether the response appears again.`,
        basis: [
          { answerRef: 'Q8 learning', paraphrase: optionLabel('learning', a.learning ?? 'REPEAT_FEELING'), basisType: 'ANSWER_PATTERN' },
          { answerRef: 'Personalisation: activity', paraphrase: pair || activity, basisType: pair ? 'SPECIFIC_REPORTED_EXAMPLE' : 'ANSWER_PATTERN' },
        ],
        asset: {
          title: 'Your test plan',
          copyButtonLabel: 'COPY MY TEST PLAN',
          readiness: 'READY_TO_USE',
          blocks: [
            { heading: 'What to repeat', body: `${activity}, kept as close to the original as you can manage.` },
            { heading: 'The features to preserve', body: 'The opening, the length, the excerpt and the amount of effort. Write these down before you make it, not after.' },
            { heading: 'Differences you cannot avoid', body: 'Date, audience mood and distribution. Note them so you do not mistake them for the result.' },
            { heading: 'Observation window', body: '7 days, matching the window you used for the original as closely as you can.' },
            { heading: 'The decision you are making', body: 'Repeat, adjust, or gather more information. Choose in advance what each outcome means, so the result is not read backwards to fit what you hoped.' },
          ],
          prerequisiteChecks: ['Write down the original attempt before you repeat it, or the comparison is from memory.'],
        },
        firstAction: { instruction: 'Prepare the closest feasible repeat and note the unavoidable differences.', approximateEffortMinutes: effort(a) },
        measurement: {
          mainObservation: 'Whether the same kind of music-specific response appears again.',
          window: '7 days, same as the original.',
          limitation: 'One good outcome followed by one repeat is a pattern of two. It is not a validated formula.',
        },
        decisionRule: {
          repeatWhen: 'The response appears again. Then, and only then, do more of it.',
          adjustWhen: 'It does not repeat. The first result may have come from distribution rather than the approach.',
          insufficientInformationWhen: 'The repeat reached a very different number of people.',
        },
        whatCanWait: { activity: 'Spending money to scale the promising attempt.', reason: 'A single unrepeated result is not yet evidence there is anything to scale.' },
        missingInputs: missing(a, 'activity_under_review'),
      };
    },
  },

  // ================= BASELINE / NO GAP =================
  B01: {
    id: 'B01',
    name: 'Shareable music, but little evidence yet',
    category: 'BASELINE',
    coreHeadline: 'GET YOUR FIRST USEFUL LISTENER SIGNALS.',
    problemAddressed: 'There are not enough attempts or observations yet to identify a bottleneck honestly.',
    triggerPatterns: [
      'response = NO_RECENT_ATTEMPT or UNKNOWN',
      'learning = TOO_EARLY',
      'widespread unknown or not-yet answers',
    ],
    gatesRequired: ['Shareable music exists', 'No confirmed concrete problem anywhere else'],
    assetProduced: 'One ready-to-make introduction in a feasible format, one valid next action, one observation and one comparable repeat.',
    mustNotClaim: ['No fabricated diagnosis', 'No demographic fan profile', 'Unknown answers are never treated as failures'],
    blockingCases: ['A confirmed destination problem (N01)', 'No shareable music (B02)'],
    personalisation: personalisationFields.BASELINE,
    build: (a) => {
      const moment = p(a, 'shareable_moment', 'the musical moment you chose');
      const method = p(a, 'sharing_method', 'PHONE_VIDEO');
      const methodText = selectLabel('BASELINE', 'sharing_method', method);
      return {
        headline: 'MAKE TWO COMPARABLE ATTEMPTS AND GET SOMETHING REAL TO WORK FROM.',
        startHereLabel: 'Start here: your first useful signals',
        startHereBody: `There is not enough evidence here to name a bottleneck, and guessing at one would waste your time. Start with one clear musical moment and a small attempt that gives you something to read.`,
        provisionalNote: 'This is a starting point, not a diagnosis. Your answers did not contain enough observed activity to identify a specific gap.',
        basis: [
          { answerRef: 'Q5 response', paraphrase: a.response.length ? optionLabel('response', a.response[0]) : 'No observations reported.', basisType: 'LIMITED_INFORMATION' },
          { answerRef: 'Q8 learning', paraphrase: optionLabel('learning', a.learning ?? 'TOO_EARLY'), basisType: 'LIMITED_INFORMATION' },
        ],
        asset: {
          title: 'Your first attempt, prepared',
          copyButtonLabel: 'COPY MY TEST PLAN',
          readiness: 'READY_TO_USE',
          blocks: [
            { heading: 'The moment', body: moment },
            { heading: 'How you will share it', body: `${methodText}. Chosen because you said you would genuinely use it.` },
            { heading: 'What to say', body: `This is a bit of ${songName(a)}. Twenty seconds.` },
            { heading: 'The one thing to record', body: 'Any reply that mentions the music, written down with the date. Counts, not impressions.' },
            { heading: 'The comparable repeat', body: `About a week later, share a different moment the same way. Same length, same method, same wording structure. Two comparable attempts are the smallest useful amount of evidence.` },
          ],
          prerequisiteChecks: ['Choose the moment before you start, so both attempts are comparable.'],
        },
        firstAction: { instruction: 'Make and share the first attempt.', approximateEffortMinutes: effort(a) },
        measurement: {
          mainObservation: 'Counted replies that mention the music.',
          window: '7 days per attempt.',
          limitation: 'Two attempts tell you very little on their own. They exist so the next result has something to work from.',
        },
        decisionRule: {
          repeatWhen: 'You made both. Make two more before changing anything.',
          adjustWhen: 'You made both and saw nothing at all. The next question is who is seeing them.',
          insufficientInformationWhen: 'You could not complete both. Reduce the attempt, not the plan.',
        },
        whatCanWait: { activity: 'Everything else, including strategy, branding and paid promotion.', reason: 'None of it can be evaluated until there is some observed activity to read.' },
        missingInputs: missing(a, 'shareable_moment'),
      };
    },
  },

  B02: {
    id: 'B02',
    name: 'No shareable material yet',
    category: 'BASELINE',
    coreHeadline: 'CHOOSE A MUSICAL MOMENT TO BUILD AROUND.',
    problemAddressed: 'There is no song or excerpt available to promote, so any promotional diagnosis would be invented.',
    triggerPatterns: ['readiness = NO_SHAREABLE'],
    gatesRequired: ['Readiness reported as NO_SHAREABLE. This gate runs first, before every other category.'],
    assetProduced: 'A concrete selection and preparation exercise matched to the material the artist actually has.',
    mustNotClaim: [
      'No diagnosis of song quality',
      'Not presented as a completed content plan',
      'No audience conclusions from nothing',
    ],
    blockingCases: ['Any shareable song or excerpt exists'],
    personalisation: personalisationFields.BASELINE,
    build: (a) => {
      const moment = p(a, 'shareable_moment');
      const blocker = p(a, 'blocker');
      const method = p(a, 'sharing_method', 'PHONE_VIDEO');
      return {
        headline: 'PICK THE TWENTY SECONDS YOU WILL BUILD EVERYTHING ELSE AROUND.',
        startHereLabel: 'Start here: choose a musical moment',
        startHereBody: `You do not yet have a shareable song or excerpt for this plan. The useful next step is choosing or preparing that material, rather than guessing why promotion is not working.`,
        provisionalNote: 'Nothing here is a judgement about your music. We have not heard it, and there is nothing yet to promote.',
        basis: [
          { answerRef: 'Q1 readiness', paraphrase: optionLabel('readiness', 'NO_SHAREABLE'), basisType: 'ANSWER_PATTERN' },
          { answerRef: 'Personalisation: blocker', paraphrase: blocker || 'No blocker described.', basisType: blocker ? 'SPECIFIC_REPORTED_EXAMPLE' : 'LIMITED_INFORMATION' },
        ],
        asset: {
          title: 'Your selection exercise',
          copyButtonLabel: 'COPY MY TEST PLAN',
          readiness: 'NEEDS_RECIPIENT_OR_DESTINATION_CHECK',
          blocks: [
            { heading: 'Step 1: list what exists', body: 'Write down every piece of music you have in any state: finished, half-finished, voice memo, live recording. Three lines maximum each.' },
            { heading: 'Step 2: choose on one criterion', body: moment ? `You already named: ${moment}. Use that.` : 'Pick the one you would be happy for a stranger to hear twenty seconds of today. Not the best one. The one that is ready.' },
            { heading: 'Step 3: prepare the twenty seconds', body: `${selectLabel('BASELINE', 'sharing_method', method)}. One recording, one take, no production. It only has to be audible.` },
            { heading: 'What this is not', body: 'This is not a content plan and not a promotional strategy. It is the material that everything else needs in order to exist.' },
            { heading: blocker ? 'The thing you said is in the way' : 'If you get stuck', body: blocker ? `You described: ${blocker}. Reduce the step until that no longer applies. A phone recording of one section counts.` : 'Reduce the step. A phone recording of one section counts.' },
          ],
          prerequisiteChecks: ['Nothing needs buying or booking to complete this.'],
        },
        firstAction: { instruction: 'Complete steps 1 and 2. Choosing is the part that unlocks everything else.', approximateEffortMinutes: Math.min(effort(a), 45) },
        measurement: {
          mainObservation: 'Whether you have twenty seconds of audio you would be willing to share.',
          window: 'One week.',
          limitation: 'There is nothing about listeners to observe yet, and any claim otherwise would be invented.',
        },
        decisionRule: {
          repeatWhen: 'You have the material. Come back and the plan can be about promotion.',
          adjustWhen: 'Choosing is the hard part. Pick the oldest one and move on.',
          insufficientInformationWhen: 'Nothing exists in any state yet. Then the work is writing and recording, not promotion.',
        },
        whatCanWait: { activity: 'All promotion, all planning, all audience thinking.', reason: 'Every one of those needs something to point at.' },
        missingInputs: missing(a, 'shareable_moment'),
      };
    },
  },

  NO_CLEAR_GAP: {
    id: 'NO_CLEAR_GAP',
    name: 'No clear reported gap',
    category: 'BASELINE',
    coreHeadline: 'BUILD ON WHAT IS ALREADY WORKING.',
    problemAddressed: 'The answers describe a coherent approach with no concrete reported problem to correct.',
    triggerPatterns: [
      'audience = SPECIFIC_REASON',
      'a deliberate discovery route',
      'song-led material',
      'nextStep = CLEAR_DESTINATION',
      'return = RELATED_FOLLOW_UP',
      'learning = COMPARE',
    ],
    gatesRequired: ['No category reaches its eligibility bar', 'No confirmed contradiction'],
    assetProduced: 'A repeatability plan, without a negative label attached.',
    mustNotClaim: [
      'Not a clean bill of health',
      'Self-reported answers cannot establish which factor would drive the next level of growth',
      'No manufactured defect to complete the quiz',
    ],
    blockingCases: ['Any category meeting its eligibility bar'],
    personalisation: personalisationFields.TESTING,
    build: (a) => {
      const activity = p(a, 'activity_under_review', 'your most relevant recent activity');
      return {
        headline: 'YOUR NEXT MOVE: BUILD ON WHAT IS ALREADY WORKING.',
        startHereLabel: 'No clear gap reported',
        startHereBody: `Your answers do not reveal a clear gap that justifies changing your whole approach. Choose the most relevant recent activity and check whether its response repeats.`,
        provisionalNote: 'This is not a clean bill of health. Self-reported answers cannot establish which factor would drive your next level of growth.',
        basis: [
          { answerRef: 'Q6 next step', paraphrase: optionLabel('nextStep', a.nextStep ?? 'CLEAR_DESTINATION'), basisType: 'ANSWER_PATTERN' },
          { answerRef: 'Q7 return', paraphrase: optionLabel('return', a.return ?? 'RELATED_FOLLOW_UP'), basisType: 'ANSWER_PATTERN' },
          { answerRef: 'Q8 learning', paraphrase: optionLabel('learning', a.learning ?? 'COMPARE'), basisType: 'ANSWER_PATTERN' },
        ],
        asset: {
          title: 'Your repeatability plan',
          copyButtonLabel: 'COPY MY TEST PLAN',
          readiness: 'READY_TO_USE',
          blocks: [
            { heading: 'The activity to repeat', body: `${activity}. Chosen because it is the one closest to ${goalPhrase(a)}.` },
            { heading: 'Hold these constant', body: 'The excerpt, the introduction structure, the destination and roughly the same effort.' },
            { heading: 'Three attempts, not one', body: 'Run it three times over three weeks. Two is a coincidence, three starts to be a pattern.' },
            { heading: 'The log', body: 'Date | What you did | Music-specific responses (count + quote) | Anything you had to change' },
            { heading: 'The decision at the end', body: 'If the response repeats, that is the approach worth investing more time in. If it does not, the earlier result came from distribution rather than the approach, and that is worth knowing too.' },
          ],
          prerequisiteChecks: ['Choose the single activity before the first attempt.'],
        },
        firstAction: { instruction: 'Run the first of the three attempts, and start the log.', approximateEffortMinutes: effort(a) },
        measurement: {
          mainObservation: 'Whether the same kind of music-specific response appears across three comparable attempts.',
          window: '7 days per attempt, three weeks total.',
          limitation: 'Organic distribution varies. A repeated response is a pattern, not a proven cause.',
        },
        decisionRule: {
          repeatWhen: 'The response repeats at least twice out of three.',
          adjustWhen: 'It appears once. Look at what differed on the other two attempts.',
          insufficientInformationWhen: 'Reach varied wildly across the three.',
        },
        whatCanWait: { activity: 'Changing the approach.', reason: 'Nothing in your answers points at a broken part, and changing a working approach removes the thing you would compare against.' },
        missingInputs: [],
      };
    },
  },
};

export const playbookOrder: PlaybookId[] = [
  'A01', 'A02', 'A03',
  'D01', 'D02', 'D03',
  'C01', 'C02', 'C03',
  'N01', 'N02', 'N03',
  'R01', 'R02', 'R03',
  'T01', 'T02', 'T03',
  'B01', 'B02', 'NO_CLEAR_GAP',
];

export const categoryLabels: Record<Category, string> = {
  AUDIENCE: 'Audience',
  DISCOVERY: 'Discovery',
  CONTENT: 'Content',
  NEXT_STEP: 'Next Step',
  RETURN: 'Return',
  TESTING: 'Testing',
  BASELINE: 'Baseline',
};

export { has };
